import {createRootContextHook} from 'common/util/react';
import {i18next} from 'common/i18n';
import type {I18nScope, TranslationKeyMap} from 'common/i18n/scope';
import {LocalizationStore, type ScopedLocalizationStore} from 'common/i18n/store';

const useRootStore = createRootContextHook(`i18n`);
const useRootStoreWithFallback = createRootContextHook(`i18n`, () => new LocalizationStore(i18next));

// Overload order matters: `ReturnType<typeof useTranslation>` resolves to the last signature, which must stay the
// root store for existing callers.
/**
 * A hook that re-renders the component when the current language changes. Pass a scope to get a store whose `t`
 * only accepts that scope's keys.
 */
export function useTranslation<Keys extends TranslationKeyMap>(scope: I18nScope<Keys>): ScopedLocalizationStore<Keys>;
export function useTranslation(): LocalizationStore;
export function useTranslation(scope?: I18nScope<any>): LocalizationStore | ScopedLocalizationStore<any> {
  const store = useRootStore();
  return scope ? store.scoped(scope) : store;
}

/**
 * Falls back to a store on the shared `i18next` instance when rendered outside a context root.
 *
 * @public
 */
export function useTranslationWithFallback<Keys extends TranslationKeyMap>(
  scope: I18nScope<Keys>,
): ScopedLocalizationStore<Keys>;
export function useTranslationWithFallback(): LocalizationStore;
export function useTranslationWithFallback(scope?: I18nScope<any>): LocalizationStore | ScopedLocalizationStore<any> {
  const store = useRootStoreWithFallback();
  return scope ? store.scoped(scope) : store;
}
